import type { ThemeMode } from "@/config/themes/types";

// ─── API ─────────────────────────────────────────────────────────

/** Header name required on every request to the Olive API. */
export const TENANT_HEADER = "tenant";

// ─── Auth & session ──────────────────────────────────────────────

/** Auth realm: tenant UI (per-tenant Cognito) vs admin UI (Olive staff Cognito). */
export const AUTH_REALM = {
  TENANT: "tenant",
  ADMIN: "admin",
} as const;

/** Per-realm Cognito token cookie names (never shared between tenant and admin). */
export const COGNITO_REALM_COOKIE = {
  [AUTH_REALM.TENANT]: {
    ID: "olive_tenant_id_token",
    REFRESH: "olive_tenant_refresh_token",
  },
  [AUTH_REALM.ADMIN]: {
    ID: "olive_admin_id_token",
    REFRESH: "olive_admin_refresh_token",
  },
} as const;

/**
 * Refresh-token cookie lifetime, matching `RefreshTokenValidity` on the Cognito app client
 * (backend `COGNITO_REFRESH_TOKEN_VALIDITY`, 120 minutes). Must never exceed it: too short forces a
 * clean re-login, too long leaves the app reporting a live session with a discarded token.
 */
export const REFRESH_TOKEN_MAX_AGE_SECONDS = 120 * 60;

/** Per-realm OAuth PKCE / state sessionStorage keys. */
export const COGNITO_REALM_OAUTH = {
  [AUTH_REALM.TENANT]: {
    PKCE_VERIFIER: "olive_pkce_verifier_tenant",
    AUTH_STATE: "olive_auth_state_tenant",
  },
  [AUTH_REALM.ADMIN]: {
    PKCE_VERIFIER: "olive_pkce_verifier_admin",
    AUTH_STATE: "olive_auth_state_admin",
  },
} as const;

/** sessionStorage key for cached SSO login-enabled flag used at login redirect. */
export const SSO_LOGIN_ENABLED_CACHE_KEY = "olive_sso_login_enabled";

/** sessionStorage flag set after logout to gate re-entry to Cognito with prompt=login. */
export const POST_LOGOUT_SESSION_KEY = "olive_post_logout";

/** sessionStorage flag marking that `/callback` already restarted login once, to bound retries. */
export const AUTH_RESTART_ATTEMPTED_KEY = "olive_auth_restart_attempted";

/** SSO configuration state values returned by `GET /v1/sso-configurations/`. */
export const SSO_STATE = {
  CONFIGURED: "configured",
  ENABLED: "enabled",
} as const;

/** LLM providers a tenant may register an API key for (`GET /v1/llm-api-keys/`). */
export const LLM_PROVIDER = {
  OPENAI: "openai",
  ANTHROPIC: "anthropic",
} as const;

/** What a catalogue model is for; groups the model picker in Settings > LLM. */
export const LLM_MODEL_KIND = {
  CHAT: "chat",
  EMBEDDING: "embedding",
  IMAGE: "image",
} as const;

/**
 * Placeholder shown in place of a stored LLM API key.
 *
 * A literal, not a truncation: the backend never returns the key or any fragment of it, so there is
 * nothing to derive a mask from. The length is arbitrary and deliberately does not match any real
 * provider key length, so it cannot be read as a hint about the stored value.
 */
export const LLM_KEY_MASK = "\u2022".repeat(32);

/** Cognito SAML IdP name provisioned by the backend. */
export const COGNITO_SAML_IDENTITY_PROVIDER = "default";

/** BroadcastChannel name used to sync session-expiry logout across open tabs. */
export const AUTH_SESSION_BROADCAST_CHANNEL = "olive_auth_session";

/** Message posted on `AUTH_SESSION_BROADCAST_CHANNEL` when a tab detects session expiry. */
export const AUTH_SESSION_EXPIRED_MESSAGE = "session-expired";

// ─── Appearance ──────────────────────────────────────────────────

/** Theme mode values. */
export const THEME_MODE = {
  DARK: "dark" as ThemeMode,
  LIGHT: "light" as ThemeMode,
} as const;

/** localStorage key for the user's theme appearance preference. */
export const APPEARANCE_STORAGE_KEY = "appearance";

// ─── Notifications ─────────────────────────────────────────────────

/** Auto-hide duration for global snackbar notifications (ms). */
export const NOTIFICATION_AUTO_HIDE_MS = 6000;

/** Variant values for the global notification snackbar. */
export const NOTIFICATION_VARIANT = {
  SUCCESS: "success",
  ERROR: "error",
} as const;

// ─── UI layout ───────────────────────────────────────────────────

/** Brand logo asset path (public URL). */
export const OLIVE_LOGO_SRC = "/brand/logo.svg";

/** How long a copy button shows its "copied" tick before reverting (ms). */
export const COPY_FEEDBACK_MS = 1600;

/** Sidebar width when expanded (px). */
export const SIDEBAR_EXPANDED_WIDTH_PX = 220;

/** Sidebar width when collapsed (px). */
export const SIDEBAR_COLLAPSED_WIDTH_PX = 56;

/** Secondary sidebar width when expanded (px). */
export const SECONDARY_SIDEBAR_EXPANDED_WIDTH_PX = 300;

/** Secondary sidebar width when collapsed (px). */
export const SECONDARY_SIDEBAR_COLLAPSED_WIDTH_PX = 56;

/** Page container padding (px). */
export const PAGE_PADDING = {
  DEFAULT: {
    LEFT_PX: 24,
    RIGHT_PX: 24,
    Y_PX: 24,
  },
  DOMAIN_DETAIL_PAGE: {
    LEFT_PX: 0,
    RIGHT_PX: 24,
    Y_PX: 16,
  },
  APP_BUILD_PAGE: {
    LEFT_PX: 0,
    RIGHT_PX: 24,
    Y_PX: 16,
  },
  HOME_PAGE: {
    LEFT_PX: 0,
    RIGHT_PX: 0,
    Y_PX: 0,
  },
} as const;

export const NUGGETS_BRACKET_SECTION_LAYOUT = {
  BRACKET_HEIGHT_PX: 70,
  BRACKET_BORDER_WIDTH_PX: 2,
  BRACKET_BORDER_RADIUS_PX: 12,
  BRACKET_OUTSET_PX: 0,
  LABEL_OFFSET_TOP_PX: -14,
  LABEL_OFFSET_LEFT_PX: 16,
  PADDING_PX: 24,
  PADDING_TOP_PX: 32,
  COLLAPSED_MARGIN_BOTTOM_PX: 24,
  EXPANDED_MARGIN_BOTTOM_PX: 32,
} as const;

/** Top offset (px) for the global snackbar so it clears header actions and the user avatar. */
export const NOTIFICATION_SNACKBAR_TOP_OFFSET_PX =
  PAGE_PADDING.DEFAULT.Y_PX + 40 + 8;

/** Stacking order for the floating user avatar overlay. */
export const LAYOUT_AVATAR_Z_INDEX = 10;

/** Horizontal space reserved so header actions do not sit under the avatar. */
export const LAYOUT_AVATAR_RESERVED_WIDTH_PX = 56;


/** Clarification chat drawer width (px) by breakpoint. */
export const CLARIFICATION_CHAT_DRAWER_WIDTH = {
  XS_PX: 400,
  SM_PX: 520,
  MD_PX: 600,
  LG_PX: 720,
} as const;

/** App builder chat drawer width (px) by breakpoint — 75% of clarification chat. */
export const APP_BUILDER_CHAT_DRAWER_WIDTH = {
  XS_PX: CLARIFICATION_CHAT_DRAWER_WIDTH.XS_PX * 0.75,
  SM_PX: CLARIFICATION_CHAT_DRAWER_WIDTH.SM_PX * 0.75,
  MD_PX: CLARIFICATION_CHAT_DRAWER_WIDTH.MD_PX * 0.75,
  LG_PX: CLARIFICATION_CHAT_DRAWER_WIDTH.LG_PX * 0.75,
} as const;

/** Placeholder email shown until real user profile is wired. */
export const DUMMY_USER_EMAIL = "user@example.com";

/** Fixed height for chart cards and table panels so they align in mixed grids (px). */
export const CHART_TABLE_PANEL_HEIGHT_PX = 320;

/** Gap between items in the app builder grid (px); also reused as the gap between two stacked KPI cards. */
export const APP_ITEMS_GRID_GAP_PX = 24;

/** Height of the drag-handle + edit/delete control row shown above every chart, table, and KPI item (px). Identical across item kinds so a stacked KPI pair lines up with a chart/table row. */
export const APP_ITEM_TOOLBAR_ROW_HEIGHT_PX = 40;

/**
 * Fixed height for a KPI metric card's content box (px). Two stacked KPI
 * cards (each under their own toolbar row) plus the gap between them add up
 * to exactly one chart/table item's total height (toolbar + panel), so a KPI
 * pair can sit flush alongside a chart or table of the same column span.
 */
export const KPI_CARD_HEIGHT_PX = Math.round(
  (CHART_TABLE_PANEL_HEIGHT_PX - APP_ITEM_TOOLBAR_ROW_HEIGHT_PX - APP_ITEMS_GRID_GAP_PX) / 2,
);

/** Fixed height for a published chart card's title/description header, or a published table card's description slot (px). Identical across both widget kinds so their total card heights always match. */
export const PUBLISHED_ITEM_HEADER_HEIGHT_PX = 56;

/** Vertical padding (top + bottom) around a published chart/table's content area (px). */
export const PUBLISHED_ITEM_CONTENT_PADDING_PX = 24;

/** 1px top + 1px bottom card border (border-box), reserved in the total so the summary's last line is never clipped by the card's own border. */
export const PUBLISHED_CARD_BORDER_PX = 2;

/** Height of ChartPanel's internal controls row — the chart-type / download / fullscreen icon buttons rendered above the chart canvas (px). A published chart is this row + a gap + the fixed canvas, so it is taller than a bare table. */
export const PUBLISHED_CHART_CONTROLS_ROW_HEIGHT_PX = 40;

/** Vertical gap between ChartPanel's controls row and its canvas (px). */
export const PUBLISHED_CHART_CONTROLS_GAP_PX = 8;

/**
 * Reserved height for a published widget's content region (px): the chart's
 * controls row + gap + the fixed chart/table canvas. Both chart and table
 * cards reserve this taller (chart-driven) region so their totals match; a
 * table simply top-aligns its shorter canvas inside it. Neither the chart nor
 * the table canvas is shrunk.
 */
export const PUBLISHED_ITEM_CONTENT_HEIGHT_PX =
  PUBLISHED_CHART_CONTROLS_ROW_HEIGHT_PX +
  PUBLISHED_CHART_CONTROLS_GAP_PX +
  CHART_TABLE_PANEL_HEIGHT_PX;

/** Number of "AI summary" body lines shown before truncating with an ellipsis; the full text is available via a tooltip on hover. */
export const PUBLISHED_SUMMARY_MAX_LINES = 3;

/** Explicit line height applied to the published item summary's label and body text (px), so the reserved summary block height below is exact rather than dependent on the typography theme. */
export const PUBLISHED_SUMMARY_LINE_HEIGHT_PX = 20;

/** Space above the summary block, separating it from the chart/table content above it (px). */
export const PUBLISHED_SUMMARY_TOP_GAP_PX = 12;

/**
 * Fixed height for a published item's "AI summary" heading + up to
 * PUBLISHED_SUMMARY_MAX_LINES of body text (px). Always reserved — even when
 * the summary is short or missing — so every published chart/table card is
 * the same height regardless of summary length.
 */
export const PUBLISHED_SUMMARY_BLOCK_HEIGHT_PX =
  PUBLISHED_SUMMARY_TOP_GAP_PX +
  PUBLISHED_SUMMARY_LINE_HEIGHT_PX +
  PUBLISHED_SUMMARY_LINE_HEIGHT_PX * PUBLISHED_SUMMARY_MAX_LINES;

/**
 * Fixed total height of a published chart or table card (px): card border +
 * header/description slot + content padding + the content region (chart
 * controls row + gap + canvas) + the summary block. Every published widget
 * card renders at this height so rows never leave a gap around shorter/taller
 * neighbors, and the full 3-line summary is always visible below the canvas.
 */
export const PUBLISHED_CARD_HEIGHT_PX =
  PUBLISHED_CARD_BORDER_PX +
  PUBLISHED_ITEM_HEADER_HEIGHT_PX +
  PUBLISHED_ITEM_CONTENT_PADDING_PX +
  PUBLISHED_ITEM_CONTENT_HEIGHT_PX +
  PUBLISHED_SUMMARY_BLOCK_HEIGHT_PX;

/**
 * Fixed height for a published KPI card (px) — half of PUBLISHED_CARD_HEIGHT_PX
 * once the gap between a stacked pair is subtracted, so two stacked published
 * KPI cards line up flush beside one published chart/table card. Distinct from
 * BUILDER_KPI_CARD_HEIGHT_PX because a published card also reserves a header
 * and content padding the builder's cards don't.
 */
export const PUBLISHED_KPI_CARD_HEIGHT_PX = Math.round(
  (PUBLISHED_CARD_HEIGHT_PX - APP_ITEMS_GRID_GAP_PX) / 2,
);

/**
 * Total height of an App Studio builder chart/table item (px): its toolbar row,
 * the fixed panel, and the AI summary block below it. The builder card is
 * content-sized rather than height-constrained, so this is a derived figure used
 * only to keep a stacked KPI pair aligned with it.
 */
export const BUILDER_ITEM_HEIGHT_PX =
  APP_ITEM_TOOLBAR_ROW_HEIGHT_PX +
  CHART_TABLE_PANEL_HEIGHT_PX +
  PUBLISHED_SUMMARY_BLOCK_HEIGHT_PX;

/**
 * Fixed height for a KPI card's content box in the App Studio builder grid (px).
 * Two stacked KPI cards — each under its own toolbar row, plus the gap between
 * them — add up to exactly one builder chart/table item, so a KPI pair sits
 * flush beside a chart or table of the same column span.
 *
 * Taller than KPI_CARD_HEIGHT_PX because the chart/table it aligns with now
 * carries an AI summary block, while a KPI has no summary of its own.
 * KPI_CARD_HEIGHT_PX stays the default for KPI cards outside that grid (the
 * chat cards panel), which have no summary block to align against.
 */
export const BUILDER_KPI_CARD_HEIGHT_PX = Math.round(
  (BUILDER_ITEM_HEIGHT_PX - 2 * APP_ITEM_TOOLBAR_ROW_HEIGHT_PX - APP_ITEMS_GRID_GAP_PX) / 2,
);

// ─── Async status ────────────────────────────────────────────────

/** Async operation status values used across Redux slices and UI. */
export const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
} as const;

// ─── Data Hub ────────────────────────────────────────────────────

/** Debounce window (ms) for batching domain entities PATCH calls. */
export const ENTITIES_PATCH_DEBOUNCE_MS = 300;

/**
 * Domain document upload rules. `MAX_BYTES_PER_FILE` mirrors the backend's own ceiling
 * (`MAX_DOCUMENT_BYTES` in `pdf_document_scanner.py`) so an oversized file fails locally instead of
 * after a wasted upload — the backend stays the authority. `MAX_FILES_PER_UPLOAD` bounds one
 * selection only; there is no cap on how many documents a domain may hold in total.
 */
export const DOMAIN_DOCUMENT_UPLOAD = {
  ACCEPTED_MIME_TYPE: "application/pdf",
  ACCEPTED_EXTENSION: ".pdf",
  MAX_BYTES_PER_FILE: 25 * 1024 * 1024,
  MAX_FILES_PER_UPLOAD: 10,
} as const;

/**
 * How often the documents tab re-reads embedding status while a run is in flight. The backend
 * answers this endpoint straight from Bedrock, so each poll is two AWS calls — slow enough to
 * stay cheap, fast enough that a short ingestion still shows progress before it finishes.
 */
export const DOMAIN_DOCUMENT_EMBEDDING_POLL_MS = 5000;

/**
 * Per-document indexing states reported by the Bedrock Knowledge Base. Only the ones the UI
 * treats specially are listed; any other status is displayed verbatim, since Bedrock's
 * vocabulary can grow without this build knowing about it.
 */
export const DOMAIN_DOCUMENT_EMBEDDING_STATUS = {
  INDEXED: "INDEXED",
  PENDING: "PENDING",
  IN_PROGRESS: "IN_PROGRESS",
  STARTING: "STARTING",
  FAILED: "FAILED",
  IGNORED: "IGNORED",
  /** The knowledge base has never seen this file — uploaded but not embedded yet. */
  NOT_FOUND: "NOT_FOUND",
} as const;

// ─── Connections ─────────────────────────────────────────────────────

/** Connection type values (API wire format). */
export const CONNECTION_TYPE = {
  SNOWFLAKES: "SNOWFLAKE",
  CUBE: "CUBE",
  MYSQL: "MYSQL",
} as const;

/** Default MySQL port when not specified in connection config. */
export const MYSQL_DEFAULT_PORT = 3306;

/** Snowflake authentication method values. */
export const SNOWFLAKE_AUTH_METHOD = {
  PRIVATE_KEY: "private_key",
  PASSWORD: "password",
} as const;

/** Cube authentication type values. */
export const CUBE_AUTH_TYPE = {
  OAUTH2_CLIENT_CREDENTIALS: "oauth2_client_credentials",
} as const;

// ─── Access management ───────────────────────────────────────────

/**
 * API permission codes, as they appear in the logged-in user's `access.permissions`.
 *
 * Mirrors `Permission` in olive-backend `src/app/security/permissions.py` 1:1 and in the same
 * order, so the two flat lists can be diffed by eye when either side changes.
 */
export const PERMISSION = {
  USER_VIEW: "user-view",
  USER_ADD: "user-add",
  USER_UPDATE: "user-update",
  USER_DELETE: "user-delete",
  ACCESS_VIEW: "access-view",
  ACCESS_ADD: "access-add",
  ACCESS_DELETE: "access-delete",
  CONNECTION_VIEW: "connection-view",
  CONNECTION_DETAIL_VIEW: "connection-detail-view",
  CONNECTION_ADD: "connection-add",
  DOMAIN_VIEW: "domain-view",
  DOMAIN_ADD: "domain-add",
  DOMAIN_UPDATE: "domain-update",
  NUGGET_CONFIG_VIEW: "nugget-config-view",
  NUGGET_CONFIG_UPDATE: "nugget-config-update",
  DOMAIN_DOCUMENT_VIEW: "domain-document-view",
  DOMAIN_DOCUMENT_UPDATE: "domain-document-update",
  SSO_VIEW: "sso-view",
  SSO_ADD: "sso-add",
  SSO_UPDATE: "sso-update",
  BUSINESS_CONTEXT_VIEW: "business-context-view",
  BUSINESS_CONTEXT_UPDATE: "business-context-update",
  LLM_KEY_VIEW: "llm-key-view",
  LLM_KEY_ADD: "llm-key-add",
  LLM_KEY_UPDATE: "llm-key-update",
  LLM_KEY_DELETE: "llm-key-delete",
  APP_VIEW: "app-view",
  APP_ADD: "app-add",
  APP_UPDATE: "app-update",
  APP_DELETE: "app-delete",
  APP_CHART_VIEW: "app-chart-view",
  APP_CHART_ADD: "app-chart-add",
  APP_CHART_UPDATE: "app-chart-update",
  APP_CHART_DELETE: "app-chart-delete",
  APP_TABLE_VIEW: "app-table-view",
  APP_TABLE_ADD: "app-table-add",
  APP_TABLE_UPDATE: "app-table-update",
  APP_TABLE_DELETE: "app-table-delete",
  APP_KPI_VIEW: "app-kpi-view",
  APP_KPI_ADD: "app-kpi-add",
  APP_KPI_UPDATE: "app-kpi-update",
  APP_KPI_DELETE: "app-kpi-delete",
  APP_QUERY_VIEW: "app-query-view",
  APP_QUERY_ADD: "app-query-add",
  APP_QUERY_UPDATE: "app-query-update",
  APP_QUERY_DELETE: "app-query-delete",
  APP_TAB_VIEW: "app-tab-view",
  APP_TAB_ADD: "app-tab-add",
  APP_TAB_UPDATE: "app-tab-update",
  APP_TAB_DELETE: "app-tab-delete",
  APP_CATEGORY_VIEW: "app-category-view",
  PUBLISHED_APP_VIEW: "published-app-view",
  CHAT_VIEW: "chat-view",
  CHAT_ADD: "chat-add",
  CHAT_UPDATE: "chat-update",
  CHAT_DELETE: "chat-delete",
  NUGGET_VIEW: "nugget-view",
  NUGGET_ADD: "nugget-add",
  NUGGET_UPDATE: "nugget-update",
  NUGGET_DELETE: "nugget-delete",
  NUGGET_GENERATE: "nugget-generate",
  NUGGET_ACTION_EXECUTE: "nugget-action-execute",
  INTEGRATION_VIEW: "integration-view",
  INTEGRATION_ADD: "integration-add",
  USER_LEARNING_GENERATE: "user-learning-generate",
  ROLE_VIEW: "role-view",
  ROLE_ADD: "role-add",
  ROLE_UPDATE: "role-update",
  DOMAIN_ACCESS_VIEW: "domain-access-view",
  DOMAIN_ACCESS_ADD: "domain-access-add",
  DOMAIN_ACCESS_DELETE: "domain-access-delete",
  NUGGET_ACTION_VIEW: "nugget-action-view",
  CONTEXT_HUB_VIEW: "context-hub-view",
  CONTEXT_HUB_UPDATE: "context-hub-update",
} as const;

export type PermissionCode = (typeof PERMISSION)[keyof typeof PERMISSION];

/**
 * The built-in Administrator role name. Its permissions are granted in backend code rather than
 * stored, so it cannot be edited — the backend rejects `PATCH /roles/{id}` for it.
 */
export const ADMINISTRATOR_ROLE_NAME = "Administrator";

// ─── Actions Hub ─────────────────────────────────────────────────

/** Actions Hub integration type values (API wire format). Slack today; more can be added later. */
export const INTEGRATION_TYPE = {
  SLACK: "SLACK",
} as const;

// ─── Learning History ────────────────────────────────────────────

/** How a Learning History insight participates in chat (API wire format). */
export const LEARNING_INSIGHT_APPLICATION_MODE = {
  APPLY_BASED_ON_CONFIDENCE: "apply_based_on_confidence",
  IGNORED: "ignored",
  TEMPORARILY_IGNORED: "temporarily_ignored",
  ALWAYS_APPLY: "always_apply",
  MANUAL_PRIORITY: "manual_priority",
} as const;

/** Confidence/priority label values (API wire format) - shared by confidence_label and
 * manual_priority_label. */
export const LEARNING_INSIGHT_PRIORITY_LABEL = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
} as const;

/** Nugget action outcome status values (API wire format; distinct from the async STATUS enum). */
export const ACTION_STATUS = {
  PENDING: "PENDING",
  SUCCESS: "SUCCESS",
  FAILED: "FAILED",
} as const;

/** How often the Actions page polls for nugget-action status updates while pending. */
export const NUGGET_ACTIONS_POLL_INTERVAL_MS = 120_000;

/**
 * Nugget severity stored in `nuggets.details.severity` (API wire format, lowercase).
 * Deliberately separate from `LEARNING_INSIGHT_PRIORITY_LABEL` despite the identical values: that is
 * a confidence scale where high is good, this is a risk scale where high is bad, so the two cannot
 * share a color map.
 */
export const NUGGET_SEVERITY = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
} as const;

export type NuggetSeverity = (typeof NUGGET_SEVERITY)[keyof typeof NUGGET_SEVERITY];

// ─── App Studio ──────────────────────────────────────────────────

/** Input-mode values for the Add/Edit chart, table, and KPI card dialogs (how the widget's SQL is authored). */
export const APP_WIDGET_INPUT_MODE = {
  QUERY: "query",
  FORM: "form",
  PROMPT: "prompt",
} as const;

// ─── Tenants ─────────────────────────────────────────────────────

/** Tenant provisioning status values from the Olive API. */
export const TENANT_STATUS = {
  PENDING: "pending",
  PROVISIONING: "provisioning",
  ACTIVE: "active",
  FAILED: "failed",
} as const;

/**
 * Supported range and default for a tenant's query-cache clear interval, in hours.
 * Mirrors the backend `app/cache/query_cache_schedule.py` bounds — the API rejects
 * anything outside MIN..MAX.
 */
export const QUERY_CACHE_CLEAR_INTERVAL_HOURS = {
  MIN: 1,
  MAX: 24,
  DEFAULT: 4,
} as const;
